// The Global room's thread (Brief 4 / P8). The Global room is ONE general,
// cross-project chat thread pinned to the top of the sidebar. There is no
// dedicated "global" backend entity yet, so we resolve it the honest way on
// live transport: find the caller's existing `group` thread titled "Global", or
// create it once. Real plumbing, no mock — the same threads API every chat uses.

import { useEffect, useRef, useState } from "react";
import { createThread, listThreads } from "../../api/client";

/** The well-known title of the general room (a `group` thread). */
export const GLOBAL_ROOM_TITLE = "Global";

export function useGlobalRoom(): {
  threadId: number | null;
  loading: boolean;
  error: string;
} {
  const [threadId, setThreadId] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  // Resolve ONCE — also guards React StrictMode's double-mount from creating
  // two "Global" threads.
  const startedRef = useRef(false);

  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;
    let alive = true;
    (async () => {
      try {
        const { threads } = await listThreads();
        // Pick the OLDEST (lowest id) "Global" group thread as canonical, so
        // every surface converges on the SAME room — even if a rare
        // simultaneous first-open created a duplicate (a harmless empty orphan),
        // both clients deterministically agree, rather than latching onto
        // different threads. Create one only when none exists.
        const canonical = threads
          .filter((t) => t.kind === "group" && t.title === GLOBAL_ROOM_TITLE)
          .sort((a, b) => a.id - b.id)[0];
        const thread =
          canonical ?? (await createThread("group", GLOBAL_ROOM_TITLE, []));
        if (alive) setThreadId(thread.id);
      } catch (e) {
        if (alive) setError(e instanceof Error ? e.message : String(e));
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  return { threadId, loading, error };
}
